import { useState } from "react";
import { Link } from "react-router-dom";
import type { ExecMySessions, ExecSessionRecord } from "@k8s-dashboard/contracts";
import { HttpError } from "@/api/client";
import { useTerminateExecSession } from "@/api/queries";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useT } from "@/i18n/I18nProvider";
import { Notice } from "@/features/drill/sessionPanel";
import { dayClock } from "@/lib/format";

/**
 * Pod 상세 Debug Terminal 카드의 "내 세션" (ADR 0030 결정 5·7)
 * --------------------------------------------------------------------------
 * 요약은 서버가 진입 화면 응답에 실어 줍니다 — 이 카드는 따로 요청하지 않습니다.
 * 세 모양만 있습니다.
 *
 * - **다른 Pod에 살아 있음** — 사용자당 상한(기본 5)에 닿았다면 여기서 열리지 않습니다. 그 Pod로 가거나
 *   이 자리에서 끝낼 수 있습니다.
 * - **이 Pod에 살아 있지만 이 탭의 세션이 아님** — 다른 탭·창에서 연 세션입니다. 재접속은 없으므로
 *   (결정 5) 이어 붙이지 않고, 끝낼 수만 있습니다.
 * - **이 Pod에서 마지막으로 끝남** — 사유(서버 문장)·시각을 남기고 "같은 대상으로 다시 열기"를
 *   한 번의 확인으로 제공합니다. 확인 창에 위험 고지를 다시 싣습니다.
 *
 * 종료 사유 문구는 서버 소유입니다(`reasonMessage`). 브라우저가 사유 코드를 문장으로 옮기지 않습니다.
 */

function podHref(r: ExecSessionRecord): string {
  const q = new URLSearchParams({ ns: r.namespace, uid: r.podUid });
  return `/pods/${encodeURIComponent(r.podName)}?${q.toString()}`;
}

export function DebugTerminalMine(props: {
  mine?: ExecMySessions;
  podUid: string;
  /** 이 탭이 마지막으로 연 세션. 같은 세션을 "다른 탭"으로 부르지 않습니다. */
  currentSessionId: string;
  /** 이 탭에서 세션이 열려 있거나 여는 중인지. 그동안은 지난 세션 안내를 접습니다. */
  busy: boolean;
  /** 위험 고지 원문(서버 소유). 다시 열기 확인 창에 그대로 싣습니다. */
  warning: string;
  onReopen: (record: ExecSessionRecord) => void;
}) {
  const { mine, podUid, currentSessionId, busy, warning, onReopen } = props;
  const { t, ready } = useT("execsession");
  const terminate = useTerminateExecSession();
  const [reopen, setReopen] = useState<ExecSessionRecord | null>(null);
  const [failure, setFailure] = useState("");

  const live = mine?.live && mine.live.sessionId !== currentSessionId ? mine.live : undefined;
  const last = !busy && !live ? mine?.lastHere : undefined;
  /* 조각은 카드가 처음 필요할 때 실립니다. 그 전에 그리면 번역 ID가 잠깐 보이고, `t`가 대체 언어
     조각까지 받으러 갑니다 — 요약은 몇백 ms 늦게 보여도 됩니다. */
  if (!ready || (!live && !last && !failure)) return null;

  const end = (r: ExecSessionRecord) => {
    setFailure("");
    terminate.mutate(r.sessionId, {
      // 403·404는 서버 문장(권한 없음·이미 끝남)을 그대로 보입니다.
      onError: (e) => setFailure(e instanceof HttpError ? e.body.message : t("execsession.mine.endFailed")),
    });
  };

  return (
    <div data-testid="exec-mine">
      {live ? (
        <Notice role="status" testId="exec-mine-live">
          {live.podUid === podUid ? (
            t("execsession.mine.liveHere", { container: live.container })
          ) : (
            <>
              {t("execsession.mine.liveElsewhere")}{" "}
              <Link to={podHref(live)} className="ds-ident" data-testid="exec-mine-live-link">
                {live.namespace}/{live.podName}
              </Link>
            </>
          )}{" "}
          <span className="muted">{t("execsession.mine.started", { at: dayClock(Date.parse(live.startedAt)) })}</span>{" "}
          {live.terminatedBy ? (
            <span className="muted">{t("execsession.mine.terminating")}</span>
          ) : (
            <button type="button" className="chip" onClick={() => end(live)} disabled={terminate.isPending} data-testid="exec-mine-end">
              {t("execsession.mine.end")}
            </button>
          )}
        </Notice>
      ) : null}
      {last ? (
        <Notice role="status" testId="exec-mine-last">
          {t("execsession.mine.lastEnded", { at: last.endedAt ? dayClock(Date.parse(last.endedAt)) : "—", container: last.container })}{" "}
          {last.reasonMessage ? <span>{last.reasonMessage}</span> : null}{" "}
          <button type="button" className="chip" onClick={() => setReopen(last)} data-testid="exec-mine-reopen">
            {t("execsession.mine.reopen")}
          </button>
        </Notice>
      ) : null}
      {failure ? (
        <Notice tone="warning" role="alert" testId="exec-mine-failure">
          {failure}
        </Notice>
      ) : null}
      <ConfirmDialog
        open={reopen !== null}
        title={t("execsession.mine.reopenTitle")}
        message={reopen ? `${t("execsession.mine.reopenTarget", { container: reopen.container, profile: reopen.profile })} ${warning}` : ""}
        confirmLabel={t("execsession.mine.reopenConfirm")}
        cancelLabel={t("execsession.mine.cancel")}
        onConfirm={() => {
          const r = reopen;
          setReopen(null);
          if (r) onReopen(r);
        }}
        onCancel={() => setReopen(null)}
      />
    </div>
  );
}
